import {
  emptyAssistantMessageMeta,
  type A2UIEnvelope,
  type AssistantMessageMeta,
  type ReasoningRow,
  type ThinkingState,
  type ToolCallStatus,
} from './types';
import { appendRow, appendStepToSubAsk } from './helpers';
import type { Action } from '../types/actions';
import type { Source } from '../types/sources';
import { isFeedbackRating } from '../types/transport';

/**
 * Loose UI message part shape — AI SDK v5 message parts. Only a few fields
 * are read here, so a structural type is enough; the full UIMessage types
 * live in `@ai-sdk/react`.
 *
 * AI SDK v5 surfaces server-emitted `data-*` events as parts on the
 * assistant message: each part has `type: 'data-<event>'` and a `data`
 * payload of the same shape as the corresponding event. Text deltas arrive
 * as `{ type: 'text', text }` parts.
 */
export interface UIMessagePart {
  type: string;
  text?: string;
  data?: Record<string, unknown>;
}

/** Fold a UI message's `parts` into AssistantMessageMeta. Pure projection —
 *  no state, no refs, no closures. Runs at render time. */
export function buildAssistantMessageMeta(
  parts: UIMessagePart[]
): AssistantMessageMeta {
  let meta = emptyAssistantMessageMeta();
  for (const part of parts) {
    meta = applyPart(meta, part);
  }
  meta = suppressEmailActionWhenA2UI(meta);
  return suppressQuoteDraftActionWhenA2UI(meta);
}

/** True when the envelope is an `updateComponents` envelope whose components
 *  array contains any element with `component === 'EmailComposer'`. A2UI v0.9
 *  declares components via `updateComponents`, NOT via `createSurface.root`. */
export function envelopeCreatesEmailComposer(env: A2UIEnvelope): boolean {
  const update = (env as { updateComponents?: { components?: unknown } })
    .updateComponents;
  const components = update?.components;
  if (!Array.isArray(components)) return false;
  return components.some(
    (c) =>
      typeof c === 'object' &&
      c !== null &&
      (c as { component?: unknown }).component === 'EmailComposer'
  );
}

function surfaceIdFor(
  env: A2UIEnvelope,
  key: 'createSurface' | 'updateComponents' | 'updateDataModel'
): string | null {
  const part = (env as Record<string, unknown>)[key];
  if (typeof part !== 'object' || part === null) return null;
  const id = (part as { surfaceId?: unknown }).surfaceId;
  return typeof id === 'string' ? id : null;
}

/** True when the envelope set contains a COMPLETE EmailComposer surface
 *  lifecycle — createSurface, updateComponents (declaring EmailComposer), and
 *  updateDataModel — all referencing the same surfaceId. Anything less means
 *  A2UISurfaceList may not be able to render a surface, so the V1 card must
 *  stay visible as a fallback. */
function hasCompleteEmailComposerSurface(
  envelopes: readonly A2UIEnvelope[]
): boolean {
  const createdSurfaceIds = new Set<string>();
  const dataModelSurfaceIds = new Set<string>();
  const componentSurfaceIds = new Set<string>();
  for (const env of envelopes) {
    const cs = surfaceIdFor(env, 'createSurface');
    if (cs) createdSurfaceIds.add(cs);
    const dm = surfaceIdFor(env, 'updateDataModel');
    if (dm) dataModelSurfaceIds.add(dm);
    if (envelopeCreatesEmailComposer(env)) {
      const uc = surfaceIdFor(env, 'updateComponents');
      if (uc) componentSurfaceIds.add(uc);
    }
  }
  for (const id of Array.from(componentSurfaceIds)) {
    if (createdSurfaceIds.has(id) && dataModelSurfaceIds.has(id)) return true;
  }
  return false;
}

/** Conflict-resolution pass: when a COMPLETE EmailComposer A2UI surface is
 *  present (createSurface + updateComponents(EmailComposer) + updateDataModel,
 *  all for the same surfaceId) AND the stream has finalized (correlationId set
 *  via data-done), drop the legacy `email`-type action card so the chat
 *  surface renders the interactive composer rather than both.
 *
 *  Why gate on correlationId: A2UISurfaceList only mounts once correlationId
 *  is set, so suppressing the V1 card before then would leave the user with
 *  no email UI at all if the stream cuts short or history lacks correlationId.
 *
 *  Why gate on the COMPLETE envelope set: a partial envelope batch (e.g. a
 *  truncated stream or persistence that dropped envelopes) lets the
 *  EmailComposer envelope through but leaves A2UISurfaceList unable to
 *  render a surface. Suppression in that case would leave the user with
 *  neither the V1 card nor an A2UI composer. Pure. */
export function suppressEmailActionWhenA2UI(
  meta: AssistantMessageMeta
): AssistantMessageMeta {
  if (meta.correlationId === null) return meta;
  if (!hasCompleteEmailComposerSurface(meta.a2uiMessages)) return meta;
  return { ...meta, actions: meta.actions.filter((a) => a.type !== 'email') };
}

/** True when the envelope is an `updateComponents` envelope declaring a
 *  component with the given name, returning that envelope's surfaceId (or
 *  null if it doesn't declare one). */
function componentsSurfaceIdFor(
  env: A2UIEnvelope,
  componentName: string
): string | null {
  const update = (
    env as { updateComponents?: { surfaceId?: unknown; components?: unknown } }
  ).updateComponents;
  const components = update?.components;
  if (!Array.isArray(components)) return null;
  const has = components.some(
    (c) =>
      typeof c === 'object' &&
      c !== null &&
      (c as { component?: unknown }).component === componentName
  );
  if (!has) return null;
  return typeof update?.surfaceId === 'string' ? update.surfaceId : null;
}

/** Returns the CopyQuote root component's declared `children` id list for a
 *  given surfaceId, or null if no CopyQuote root is declared for it.
 *
 *  Scans every envelope rather than returning on the first match — if the
 *  agent ever re-sends `updateComponents` for the same surfaceId (e.g. the
 *  root declared with an empty `children: []` before options stream in,
 *  then a later envelope with the real list), the LAST declaration wins.
 *  Returning on first match would permanently pin `children` to that first,
 *  possibly-empty snapshot and `hasCompleteCopyQuoteSurface` would never
 *  become true even once the surface is fully rendered. */
function copyQuoteChildIdsFor(
  envelopes: readonly A2UIEnvelope[],
  surfaceId: string
): readonly string[] | null {
  let childIds: readonly string[] | null = null;
  for (const env of envelopes) {
    const update = (
      env as {
        updateComponents?: { surfaceId?: unknown; components?: unknown };
      }
    ).updateComponents;
    if (update?.surfaceId !== surfaceId || !Array.isArray(update?.components)) {
      continue;
    }
    const root = update.components.find(
      (c) =>
        typeof c === 'object' &&
        c !== null &&
        (c as { component?: unknown }).component === 'CopyQuote'
    ) as { children?: unknown } | undefined;
    if (!root) continue;
    childIds = Array.isArray(root.children)
      ? root.children.filter((id): id is string => typeof id === 'string')
      : [];
  }
  return childIds;
}

/** True when every id in a surfaceId's CopyQuote `children` list has a
 *  matching QuoteOption declared somewhere in `updateComponents` for that
 *  same surfaceId. A partial batch (root present, an option missing) must
 *  not suppress the V1 card — same reasoning as
 *  `hasCompleteEmailComposerSurface`, just a stronger condition since
 *  CopyQuote is multi-component (root + N options) where Email is one. */
function hasCompleteCopyQuoteSurface(
  envelopes: readonly A2UIEnvelope[]
): boolean {
  const createdSurfaceIds = new Set<string>();
  const dataModelSurfaceIds = new Set<string>();
  const copyQuoteSurfaceIds = new Set<string>();
  for (const env of envelopes) {
    const cs = surfaceIdFor(env, 'createSurface');
    if (cs) createdSurfaceIds.add(cs);
    const dm = surfaceIdFor(env, 'updateDataModel');
    if (dm) dataModelSurfaceIds.add(dm);
    const cq = componentsSurfaceIdFor(env, 'CopyQuote');
    if (cq) copyQuoteSurfaceIds.add(cq);
  }
  for (const surfaceId of Array.from(copyQuoteSurfaceIds)) {
    if (
      !createdSurfaceIds.has(surfaceId) ||
      !dataModelSurfaceIds.has(surfaceId)
    ) {
      continue;
    }
    const childIds = copyQuoteChildIdsFor(envelopes, surfaceId) ?? [];
    const declaredOptionIds = new Set<string>();
    for (const env of envelopes) {
      const update = (
        env as {
          updateComponents?: { surfaceId?: unknown; components?: unknown };
        }
      ).updateComponents;
      if (
        update?.surfaceId !== surfaceId ||
        !Array.isArray(update?.components)
      ) {
        continue;
      }
      for (const c of update.components) {
        if (
          typeof c === 'object' &&
          c !== null &&
          (c as { component?: unknown }).component === 'QuoteOption' &&
          typeof (c as { id?: unknown }).id === 'string'
        ) {
          declaredOptionIds.add((c as { id: string }).id);
        }
      }
    }
    // childIds.every(...) is vacuously true on an empty list — guard against
    // that explicitly, or a CopyQuote root with no options yet (declared
    // before its children stream in) would count as "complete" and suppress
    // the V1 fallback card with nothing to replace it.
    if (
      childIds.length > 0 &&
      childIds.every((id) => declaredOptionIds.has(id))
    ) {
      return true;
    }
  }
  return false;
}

/** Conflict-resolution pass for CopyQuote, mirroring
 *  `suppressEmailActionWhenA2UI` — drops the legacy `form`/`quote-draft`
 *  action card once a COMPLETE CopyQuote A2UI surface (root + every
 *  referenced QuoteOption + createSurface + updateDataModel, all for the
 *  same surfaceId) is present and the stream has finalized. */
export function suppressQuoteDraftActionWhenA2UI(
  meta: AssistantMessageMeta
): AssistantMessageMeta {
  if (meta.correlationId === null) return meta;
  if (!hasCompleteCopyQuoteSurface(meta.a2uiMessages)) return meta;
  return {
    ...meta,
    actions: meta.actions.filter(
      (a) => !(a.type === 'form' && a.subType === 'quote-draft')
    ),
  };
}

function applyPart(
  prev: AssistantMessageMeta,
  part: UIMessagePart
): AssistantMessageMeta {
  const data = part.data ?? {};
  switch (part.type) {
    case 'data-thinking':
      return handleThinking(prev, data);
    case 'data-sub-ask-start':
      return handleSubAskStart(prev, data);
    case 'data-sub-ask-end':
      return handleSubAskEnd(prev, data);
    case 'data-tool-call':
      return handleToolCall(prev, data);
    case 'data-skill-activation':
      return handleSkillActivation(prev, data);
    case 'data-reasoning':
      return handleReasoning(prev, data);
    case 'data-done':
      return handleDone(prev, data);
    case 'data-feedback-rating':
      return handleFeedbackRating(prev, data);
    case 'data-actions':
      return handleActions(prev, data);
    case 'data-sources':
      return handleSources(prev, data);
    case 'data-sources-summary':
      return handleSourcesSummary(prev, data);
    case 'data-a2ui':
      return handleA2UI(prev, data);
    default:
      // text parts, unknown part types — leave meta untouched
      return prev;
  }
}

function handleActions(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const raw = data.actions;
  if (!Array.isArray(raw)) return prev;
  return { ...prev, actions: [...prev.actions, ...(raw as Action[])] };
}

function handleSources(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const raw = data.sources;
  if (!Array.isArray(raw)) return prev;
  return { ...prev, sources: [...prev.sources, ...(raw as Source[])] };
}

function handleA2UI(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  return {
    ...prev,
    a2uiMessages: [...prev.a2uiMessages, data as A2UIEnvelope],
  };
}

function handleSourcesSummary(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  // Backend emits snake_case (`kb_docs`, `data_queries`, `other`); the
  // typed shape on this side is camelCase. Accept both — the camelCase
  // value wins when present so callers that already pre-normalize aren't
  // surprised.
  const kbDocs = pickInt(data, 'kbDocs', 'kb_docs');
  const dataQueries = pickInt(data, 'dataQueries', 'data_queries');
  const other = pickInt(data, 'other');
  return { ...prev, sourcesSummary: { kbDocs, dataQueries, other } };
}

function pickInt(src: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const v = src[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

function handleFeedbackRating(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const raw = data.rating;
  if (raw === null) {
    return { ...prev, feedbackRating: null };
  }
  if (isFeedbackRating(raw)) {
    return { ...prev, feedbackRating: raw };
  }
  return prev;
}

// ───────────────────────────────── per-part handlers ──

function handleThinking(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as {
    title?: string;
    step: string;
    stepIndex?: number;
    skillName?: string | null;
    subAskId?: string;
    groupKey?: string;
    groupLabel?: string;
  };
  if (d.subAskId) {
    return appendStepToSubAsk(prev, d.subAskId, d.step, {
      title: d.title,
      groupKey: d.groupKey,
      groupLabel: d.groupLabel,
    });
  }
  return appendRow(
    prev,
    (pendingReasoning) => ({
      kind: 'reasoning',
      stepIndex: d.stepIndex ?? 0,
      text: d.step,
      skillName: d.skillName ?? null,
      groupKey: d.groupKey,
      groupLabel: d.groupLabel,
      reasoning: pendingReasoning,
    }),
    d.step,
    { title: d.title }
  );
}

function handleSubAskStart(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as { subAskId: string; label?: string; title?: string };
  const list = prev.subAskThinkings;
  const idx = list.findIndex((t) => t.subAskId === d.subAskId);
  if (idx !== -1) {
    const existing = list[idx];
    if (existing) {
      const next = list.slice();
      next[idx] = {
        ...existing,
        label: d.label ?? existing.label,
        title: d.title ?? existing.title,
      };
      return { ...prev, subAskThinkings: next };
    }
  }
  const created: ThinkingState = {
    title: d.title ?? d.label ?? 'Reasoning',
    label: d.label,
    rows: [],
    pendingReasoning: '',
    isComplete: false,
    subAskId: d.subAskId,
  };
  return { ...prev, subAskThinkings: [...list, created] };
}

function handleSubAskEnd(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as {
    subAskId: string;
    status: 'ok' | 'failed';
    reason?: string;
  };
  const list = prev.subAskThinkings;
  const idx = list.findIndex((t) => t.subAskId === d.subAskId);
  const finalRow: ReasoningRow | null =
    d.status === 'failed'
      ? {
          kind: 'reasoning',
          stepIndex: 0,
          text: `⚠ Couldn't complete: ${d.reason ?? 'unknown error'}`,
        }
      : null;
  if (idx === -1) {
    if (!finalRow) return prev;
    const created: ThinkingState = {
      title: 'Reasoning',
      rows: [finalRow],
      pendingReasoning: '',
      isComplete: true,
      subAskId: d.subAskId,
    };
    return { ...prev, subAskThinkings: [...list, created] };
  }
  const existing = list[idx];
  if (!existing) return prev;
  const updated: ThinkingState = {
    ...existing,
    rows: finalRow
      ? [...existing.rows, { ...finalRow, stepIndex: existing.rows.length }]
      : existing.rows,
    isComplete: true,
  };
  const next = list.slice();
  next[idx] = updated;
  return { ...prev, subAskThinkings: next };
}

function handleToolCall(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as {
    phase: 'start' | 'end';
    toolCallId: string;
    toolName?: string;
    displayName?: string;
    skillName?: string | null;
    stepIndex?: number;
    status?: 'ok' | 'error';
  };
  if (d.phase === 'start') {
    const displayName = d.displayName ?? d.toolName ?? '';
    return appendRow(
      prev,
      (pendingReasoning) => ({
        kind: 'tool',
        stepIndex: d.stepIndex ?? 0,
        toolCallId: d.toolCallId,
        toolName: d.toolName ?? '',
        displayName,
        skillName: d.skillName ?? null,
        status: 'running',
        reasoning: pendingReasoning,
      }),
      displayName
    );
  }
  if (!prev.thinking) return prev;
  const nextStatus: ToolCallStatus = d.status === 'error' ? 'error' : 'ok';
  const rows = prev.thinking.rows.map((r) =>
    r.kind === 'tool' && r.toolCallId === d.toolCallId
      ? { ...r, status: nextStatus }
      : r
  );
  return { ...prev, thinking: { ...prev.thinking, rows } };
}

function handleSkillActivation(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as {
    skillName: string;
    skillId?: string | null;
    stepIndex?: number;
  };
  const next = appendRow(
    prev,
    (pendingReasoning) => ({
      kind: 'skill',
      stepIndex: d.stepIndex ?? 0,
      skillName: d.skillName,
      reasoning: pendingReasoning,
    }),
    `Activated skill: ${d.skillName}`
  );
  if (!next.thinking) return next;
  return {
    ...next,
    thinking: {
      ...next.thinking,
      skillId: d.skillId ?? next.thinking.skillId ?? null,
    },
  };
}

function handleReasoning(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as { delta?: string; final?: boolean };
  const buffer = appendDeltaToBuffer(
    prev.thinking?.pendingReasoning ?? '',
    d.delta
  );
  if (d.final === true) {
    return finalizeReasoning(prev, buffer);
  }
  return bufferReasoning(prev, buffer);
}

function appendDeltaToBuffer(
  prevBuffer: string,
  delta: string | undefined
): string {
  return delta ? `${prevBuffer}${delta}` : prevBuffer;
}

/** Streaming-in-progress branch: keep buffering into pendingReasoning, creating
 *  a fresh ThinkingState if none exists yet. */
function bufferReasoning(
  prev: AssistantMessageMeta,
  buffer: string
): AssistantMessageMeta {
  const thinking: ThinkingState = prev.thinking
    ? { ...prev.thinking, pendingReasoning: buffer }
    : freshReasoningState(buffer, false);
  return { ...prev, thinking };
}

/** Final-delta branch: try to attach buffered reasoning onto the last row, or
 *  retain it as a tail. */
function finalizeReasoning(
  prev: AssistantMessageMeta,
  buffer: string
): AssistantMessageMeta {
  const trimmed = buffer.trim();
  if (!prev.thinking) {
    return {
      ...prev,
      thinking: freshReasoningState(trimmed ? buffer : '', true),
    };
  }
  const attached = tryAttachReasoningToLastRow(prev.thinking, buffer, trimmed);
  return { ...prev, thinking: attached };
}

function tryAttachReasoningToLastRow(
  thinking: ThinkingState,
  buffer: string,
  trimmed: string
): ThinkingState {
  const lastIdx = thinking.rows.length - 1;
  const last = lastIdx >= 0 ? thinking.rows[lastIdx] : undefined;
  if (!trimmed || !last) {
    return {
      ...thinking,
      pendingReasoning: trimmed ? buffer : '',
      reasoningDone: true,
    };
  }
  const merged = last.reasoning
    ? `${last.reasoning}\n\n${buffer}`.replace(/\n{3,}/g, '\n\n')
    : buffer;
  const updated: ReasoningRow = { ...last, reasoning: merged };
  return {
    ...thinking,
    rows: [...thinking.rows.slice(0, lastIdx), updated],
    pendingReasoning: '',
    reasoningDone: true,
  };
}

function freshReasoningState(
  pendingReasoning: string,
  reasoningDone: boolean
): ThinkingState {
  return {
    title: 'Reasoning',
    rows: [],
    skillId: null,
    pendingReasoning,
    reasoningDone,
    isComplete: false,
  };
}

function handleDone(
  prev: AssistantMessageMeta,
  data: Record<string, unknown>
): AssistantMessageMeta {
  const d = data as { correlationId?: string; messageId?: string };
  return {
    ...prev,
    thinking: prev.thinking ? { ...prev.thinking, isComplete: true } : null,
    subAskThinkings: prev.subAskThinkings.map((t) => ({
      ...t,
      isComplete: true,
    })),
    correlationId: d.correlationId ?? prev.correlationId,
    serverMessageId: d.messageId ?? prev.serverMessageId,
  };
}
